import { useMutation } from '@apollo/client/react';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { css } from '@styled-system/css';
import { useAtomValue } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import React, { useCallback } from 'react';

import { Alert, Button, EntryList, Modal, Text } from '~/components/blocks';
import {
  CreateMessageForLineVisitorDocument,
  GetReceptionListAppointmentDocument,
  SkipEncounterDocument,
} from '~/graphql/generated/graphql';
import { useOrganizationElectronicPrescription } from '~/hooks/use-organization-electronic-prescription';
import { lineVisitorNotificationConfirmModalState } from '~/state/reception/atoms';

import { useFetchLineVisitorAndOrganizationName } from '../use-fetch_line_visitor_and_organization_name';

const Message = styled('header')(({ theme }) =>
  css({
    whiteSpace: 'pre-wrap',
    background: theme.colors.background.bg,
    padding: theme.space.l,
  }),
);

export const NotificationConfirmModal = () => {
  const theme = useTheme();
  const { appointmentId, lineVisitorId, isOpen } = useAtomValue(
    lineVisitorNotificationConfirmModalState,
  );
  const resetNotificationConfirmModalState = useResetAtom(lineVisitorNotificationConfirmModalState);

  const { patientName, organizationName } = useFetchLineVisitorAndOrganizationName({
    appointmentId: appointmentId,
  });
  const [skipEncounter, { loading: skipping }] = useMutation(SkipEncounterDocument, {
    refetchQueries: [
      {
        query: GetReceptionListAppointmentDocument,
        variables: { appointmentId: appointmentId },
      },
    ],
  });
  const [createMessage, { loading: creating, error: error }] = useMutation(
    CreateMessageForLineVisitorDocument,
  );
  const { electronicPrescription } = useOrganizationElectronicPrescription();
  const content = electronicPrescription
    ? `${patientName}様\n\n${organizationName}です。お薬の受け渡し準備ができました。処方箋の原本または処方内容（控え）をお持ちのうえ薬局にお越しください。`
    : `${patientName}様\n\n${organizationName}です。お薬の受け渡し準備ができました。処方箋の原本をお持ちのうえ薬局にお越しください。`;

  const handleClose = useCallback(
    () => resetNotificationConfirmModalState(),
    [resetNotificationConfirmModalState],
  );

  const handleSend = useCallback(async () => {
    if (lineVisitorId) {
      await createMessage({
        variables: {
          input: {
            lineVisitorId: lineVisitorId,
            content,
          },
        },
      });

      if (appointmentId) {
        // 指導待ち -> 未会計に変更
        await skipEncounter({
          variables: {
            input: {
              appointmentId: appointmentId,
            },
          },
        });
      }

      resetNotificationConfirmModalState();
    }
  }, [
    content,
    createMessage,
    lineVisitorId,
    appointmentId,
    skipEncounter,
    resetNotificationConfirmModalState,
  ]);

  return (
    <Modal open={isOpen} size="s" onClose={handleClose}>
      <Modal.Header>準備完了通知を送信</Modal.Header>
      <Modal.Body>
        {error && <Alert mb={theme.space.l}>エラーが発生したため送信できませんでした</Alert>}
        <Text>メッセージ内容を確認のうえ、「送信」ボタンをクリックしてください。</Text>
        <EntryList mt={theme.space.m}>
          <EntryList.Head>送信メッセージ</EntryList.Head>
          <EntryList.Body>
            <Message>{content}</Message>
          </EntryList.Body>
        </EntryList>
      </Modal.Body>
      <Modal.Footer>
        <Button use="secondary" loading={creating || skipping} onClick={handleSend}>
          送信
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
